import { formatMoney } from 'src/utils/formatters';
import { useState } from 'react';
import { Percent, DollarSign, Check, X } from 'lucide-react';

interface ValuePreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // onSave: (value: {
  //   isDepositSent: boolean;
  //   depositPaidPercent: number;
  //   depositPaid: number;
  //   remainingBalance: number;
  // }) => void;
  onSave: any;
  initialValue?: { type: 'percentage' | 'fixed'; amount: number };
  grandTotal: number;
}

export function DepositModal({
  open,
  onOpenChange,
  onSave,
  initialValue,
  grandTotal
}: ValuePreviewModalProps) {
  const [valueType, setValueType] = useState<'percentage' | 'fixed'>(
    initialValue?.type || 'percentage'
  );
  const [amount, setAmount] = useState<string>(
    initialValue?.amount?.toString() || ''
  );

  const calculateDepositAmount = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 0;

    if (valueType === 'percentage') {
      return (grandTotal * numAmount) / 100;
    }
    return numAmount;
  };

  const calculateRemainingAmount = () => {
    return grandTotal - calculateDepositAmount();
  };

  const handleSave = () => {
    const numAmount = parseFloat(amount);
    if (!isNaN(numAmount)) {
      const depositAmount = calculateDepositAmount();
      onSave({
        type: valueType,
        amount: depositAmount,
        depositAmount: depositAmount,
        // // isDepositSent: true,
        // depositPaidPercent: (depositAmount / grandTotal) * 100,
        // depositPaid: depositAmount,
        // remainingBalance: calculateRemainingAmount()
      });

      onOpenChange(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const isValid = amount && !isNaN(parseFloat(amount));
  // amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 " onClick={handleClose} />

      {/* Modal Content */}
      <div className="relative w-full max-w-md mx-4 bg-card/95 shadow-2xl rounded-lg border border-border/50 p-6 bg-white">
        {/* Header */}
        <div className="space-y-4 mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            Set Deposit Amount
          </h2>
          {/* <div className="text-sm text-muted-foreground">
            Grand Total:{' '}
            <span className="font-semibold text-foreground">
              ${grandTotal.toFixed(2)}
            </span>
          </div> */}

          {/* Type Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setValueType('percentage')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                valueType === 'percentage'
                  ? 'bg-gradient-to-br from-[#E8BF21] to-[#000000] text-white shadow-glow'
                  : 'bg-secondary/50 text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <Percent className="h-4 w-4" />
              Percentage
            </button>
            <button
              onClick={() => setValueType('fixed')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                valueType === 'fixed'
                  ? 'bg-gradient-to-br from-[#E8BF21] to-[#000000] text-white shadow-glow'
                  : 'bg-secondary/50 text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              <DollarSign className="h-4 w-4" />
              Fixed Amount
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="space-y-6 mb-6">
          <div className="space-y-3">
            <label
              htmlFor="amount"
              className="block text-sm font-medium text-muted-foreground"
            >
              Enter {valueType === 'percentage' ? 'percentage' : 'amount'}
            </label>
            <div className="relative">
              <input
                id="amount"
                type="number"
                placeholder={
                  valueType === 'percentage' ? 'e.g. 15' : 'e.g. 100'
                }
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-3 py-2 text-lg font-medium bg-secondary/50 border border-border/50 rounded-md focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 pr-10"
                step={valueType === 'percentage' ? '0.1' : '1'}
                min="0"
                style={{
                      WebkitAppearance: 'textfield',
                                              MozAppearance: 'textfield'
                }}
              />
                <style>{`
                                            input[type="number"]::-webkit-outer-spin-button,
                                            input[type="number"]::-webkit-inner-spin-button {
                                              -webkit-appearance: none;
                                              margin: 0;
                                            }
                                          `}</style>
              {valueType === 'percentage' && (
                <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              )}
              {valueType === 'fixed' && (
                <DollarSign className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Preview */}
          {/* {isValid && ( */}
          <div className="p-4 rounded-lg bg-success/10 border border-success/20 space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-success/20 text-success border border-success/30">
                Preview
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Grand Total:</span>
                <span className="font-semibold text-success">
                  {formatMoney(grandTotal)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Deposit Amount:</span>
                <span className="font-semibold text-success">
                  {formatMoney(calculateDepositAmount())}
                </span>
              </div>
              <hr className="border-t border-border/50" />
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Remaining Balance:
                </span>
                <span className="font-semibold text-foreground">
                  {formatMoney(calculateRemainingAmount())}
                </span>
              </div>
            </div>
          </div>
          {/* )} */}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-border/50 text-foreground hover:bg-secondary/50 transition-colors"
          >
            <X className="h-4 w-4" />
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-gradient-to-br from-[#E8BF21] to-[#000000] text-white hover:from-primary/90 hover:to-success/90 shadow-glow disabled:opacity-50 disabled:shadow-none transition-all"
          >
            <Check className="h-4 w-4" />
            Apply Deposit
          </button>
        </div>
      </div>
    </div>
  );
}
